/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button } from '@chakra-ui/react';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactExport from 'react-export-excel';
import { useTranslation } from 'react-i18next';
import c from '../../../constant';
import useMoment from '../../../hooks/useMoment';
import useStore from '../../../store';
import { EWSTypeTemplate } from '../../../utils/ews';

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

const toStr = (value) => String(value ?? '');

export default function Export({ period = null, type = null, reportId = null, year = null, objects = [], ...props }) {
	const { t } = useTranslation();
	const { dateNow, toDateString } = useMoment();
	const { currentLang } = useStore((state) => state.currentLang);

	function generateFileName(reportId, year) {
		const q = period === 'quarterly' ? type : null;
		const date = toDateString(dateNow(), c.fileNameDateFormat, 'en-gb');
		if (q) {
			return `${reportId}_${q}_${year}_generated_at_${date}`;
		}
		return `${reportId}_${year}_generated_at_${date}`;
	}

	return (
		<Box {...props}>
			<ExcelFile
				filename={generateFileName(reportId, year)}
				element={
					<Button
						leftIcon={<FontAwesomeIcon icon={faFileExcel} style={{ fontSize: 16 }} />}
						colorScheme="gray"
						variant="solid"
						size="sm"
						borderRadius={c.borderRadius}
						border="1px solid #bdc3c7"
					>
						{t('Export')}
					</Button>
				}
			>
				{objects?.map((object, i) => {
					if (
						object?.code === EWSTypeTemplate.SFP ||
						object?.code === EWSTypeTemplate.SPLOCI ||
						object?.code === EWSTypeTemplate.SCF ||
						object?.code === EWSTypeTemplate.ORD
					) {
						const data =
							object?.labels?.map((label, j) => ({
								title: toStr(currentLang === 'kh' ? label?.titleKhmer : label?.titleEnglish),
								valueUSD: toStr(object?.cells?.[j]?.[0]?.value),
								valueKHR: toStr(object?.cells?.[j]?.[1]?.value),
							})) || [];

						return (
							<ExcelSheet key={i} data={data} name={object?.code}>
								<ExcelColumn label={t('Label')} value="title" />
								<ExcelColumn label={t('USD')} value="valueUSD" />
								<ExcelColumn label={t("KHR'000")} value="valueKHR" />
							</ExcelSheet>
						);
					}

					if (object?.code === EWSTypeTemplate.IR) {
						const data =
							object?.labels?.map((label, j) => ({
								title: toStr(currentLang === 'kh' ? label?.titleKhmer : label?.titleEnglish),
								valueKHRIMR: toStr(object?.cells?.[j]?.[0]?.value),
								valueFRIMR: toStr(object?.cells?.[j]?.[1]?.value),
								valueTotalIMR: toStr(object?.cells?.[j]?.[2]?.value),
								valueUSD: toStr(object?.cells?.[j]?.[3]?.value),
								valuePercentage: toStr(object?.cells?.[j]?.[4]?.value),
							})) || [];

						return (
							<ExcelSheet key={i} data={data} name={object?.code}>
								<ExcelColumn label={t('Label')} value="title" />
								<ExcelColumn label={currentLang === 'kh' ? 'ប្រាក់រៀល (គិតជា លានរៀល)' : 'KHR (In Million KHR)'} value="valueKHRIMR" />
								<ExcelColumn
									label={currentLang === 'kh' ? 'រូបិយវត្ថុអន្តរជាតិ (គិតជា លានរៀល)' : 'Foreign Currency (In Million KHR)'}
									value="valueFRIMR"
								/>
								<ExcelColumn label={currentLang === 'kh' ? 'សរុប (គិតជា លានរៀល)' : 'Total (In Million KHR)'} value="valueTotalIMR" />
								<ExcelColumn label={t('USD')} value="valueUSD" />
								<ExcelColumn label={t('Percentage of Invested Asset')} value="valuePercentage" />
							</ExcelSheet>
						);
					}

					if (object?.code === EWSTypeTemplate.SOCE) {
						const data =
							object?.labels?.map((label, j) => ({
								title: toStr(currentLang === 'kh' ? label?.titleKhmer : label?.titleEnglish),
								valueUSDSC: toStr(object?.cells?.[j]?.[0]?.value),
								valueKHRSC: toStr(object?.cells?.[j]?.[1]?.value),
								valueUSDSP: toStr(object?.cells?.[j]?.[2]?.value),
								valueKHRSP: toStr(object?.cells?.[j]?.[3]?.value),
								valueUSDDFSS: toStr(object?.cells?.[j]?.[4]?.value),
								valueKHRDFSS: toStr(object?.cells?.[j]?.[5]?.value),
								valueUSDRR: toStr(object?.cells?.[j]?.[6]?.value),
								valueKHRRR: toStr(object?.cells?.[j]?.[7]?.value),
								valueUSDRA: toStr(object?.cells?.[j]?.[8]?.value),
								valueKHRRA: toStr(object?.cells?.[j]?.[9]?.value),
								valueUSDOCE: toStr(object?.cells?.[j]?.[10]?.value),
								valueKHROCE: toStr(object?.cells?.[j]?.[11]?.value),
								valueUSDTOTAL: toStr(object?.cells?.[j]?.[12]?.value),
								valueKHRTOTAL: toStr(object?.cells?.[j]?.[13]?.value),
							})) || [];

						return (
							<ExcelSheet key={i} data={data} name={object?.code}>
								<ExcelColumn label={t('Label')} value="title" />
								<ExcelColumn label={currentLang === 'kh' ? 'ដុល្លារ​អាមេរិក (ដើមទុន)' : 'USD (Share Capital)'} value="valueUSDSC" />
								<ExcelColumn label={currentLang === 'kh' ? 'ពាន់រៀល (ដើមទុន)' : "KHR'000 (Share Capital)"} value="valueKHRSC" />
								<ExcelColumn label={currentLang === 'kh' ? 'ដុល្លារ​អាមេរិក (បុព្វលាភភាគហ៊ុន)' : 'USD (Share Premium)'} value="valueUSDSP" />
								<ExcelColumn label={currentLang === 'kh' ? 'ពាន់រៀល (បុព្វលាភភាគហ៊ុន)' : "KHR'000 (Share Premium)"} value="valueKHRSP" />
								<ExcelColumn
									label={
										currentLang === 'kh'
											? 'ដុល្លារ​អាមេរិក (ប្រាក់កក់សម្រាប់ទិញភាគហ៊ុនពេលអនាគត)'
											: 'USD (Deposits for future share subscription)'
									}
									value="valueUSDDFSS"
								/>
								<ExcelColumn
									label={
										currentLang === 'kh'
											? 'ពាន់រៀល (ប្រាក់កក់សម្រាប់ទិញភាគហ៊ុនពេលអនាគត)'
											: "KHR'000 (Deposits for future share subscription)"
									}
									value="valueKHRDFSS"
								/>
								<ExcelColumn
									label={currentLang === 'kh' ? 'ដុល្លារ​អាមេរិក (ប្រាក់បម្រុងដែលត្រូវបានវាយតម្លៃឡើងវិញ)' : 'USD (Revaluation Reserves)'}
									value="valueUSDRR"
								/>
								<ExcelColumn
									label={currentLang === 'kh' ? 'ពាន់រៀល (ប្រាក់បម្រុងដែលត្រូវបានវាយតម្លៃឡើងវិញ)' : "KHR'000 (Revaluation Reserves)"}
									value="valueKHRRR"
								/>
								<ExcelColumn
									label={currentLang === 'kh' ? 'ដុល្លារ​អាមេរិក (ចំណេញរក្សាទុក/ខាតបង្គរ)' : 'USD (Retained earnings/accumulated losses)'}
									value="valueUSDRA"
								/>
								<ExcelColumn
									label={currentLang === 'kh' ? 'ពាន់រៀល (ចំណេញរក្សាទុក/ខាតបង្គរ)' : "KHR'000 (Retained earnings/accumulated losses)"}
									value="valueKHRRA"
								/>
								<ExcelColumn
									label={currentLang === 'kh' ? 'ដុល្លារ​អាមេរិក (សមាសធាតុផ្សេងទៀតនៃមូលធន)' : 'USD (Other components of equity)'}
									value="valueUSDOCE"
								/>
								<ExcelColumn
									label={currentLang === 'kh' ? 'ពាន់រៀល (សមាសធាតុផ្សេងទៀតនៃមូលធន)' : "KHR'000 (Other components of equity)"}
									value="valueKHROCE"
								/>
								<ExcelColumn label={currentLang === 'kh' ? 'ដុល្លារ​អាមេរិក (សរុប)' : 'USD (Total)'} value="valueUSDTOTAL" />
								<ExcelColumn label={currentLang === 'kh' ? 'ពាន់រៀល (សរុប)' : "KHR'000 (Total)"} value="valueKHRTOTAL" />
							</ExcelSheet>
						);
					}

					return null;
				})}
			</ExcelFile>
		</Box>
	);
}
